import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import Svg from "@natera/material/lib/svg";
import { Button } from "@natera/form";
import { usePhoneValidation } from "@natera/platform/lib/hooks";
import { SpinnerView } from "@app/components";
import { ConfigContext } from "@app/provider";
import {
  formatPastedPhoneNumber,
  formatPhoneNumberInput,
  stripPhoneFormatting,
  validateEmail,
} from "@app/utils";
import ArrowBack from "@app/postBill/assets/svg/arrowNavBack.svg";
import { routes } from "@app/routing";
import {
  classifyCounselingIntent,
  GisIntakeStep,
  STEP_ROUTE_SUFFIX,
} from "@app/service/intakeForm";
import { useGisIntakeFormData } from "@app/hooks/useGisIntakeFormData";
import {
  useIntakeForm,
  useGisGuest,
  readGuestLookupPrefill,
  writeGuestLookupPrefill,
} from "@app/pages/private/scheduleAGeneticInformationSession/context";
import { useGisSessionOps } from "@app/pages/private/scheduleAGeneticInformationSession/useGisSessionOps";
import { buildLookupPrefilledInput, resolveLookupReturnUrl } from "../helpers";
import "@app/pages/private/scheduleAGeneticInformationSession/intakeFormConfirmInfo.scss";

const messages = defineMessages({
  pageTitle: {
    id: "guestIntakeConfirmInfo.pageTitle",
    defaultMessage: "Genetic Information Session",
  },
  stepIndicator: {
    id: "guestIntakeConfirmInfo.stepIndicator",
    defaultMessage: "Step {current} of {total}",
  },
  mainTitle: {
    id: "guestIntakeConfirmInfo.mainTitle",
    defaultMessage:
      "We'll help you schedule the appointment. First, please confirm or update your contact information.",
  },
  mobileNumber: {
    id: "guestIntakeConfirmInfo.mobileNumber",
    defaultMessage: "Mobile number",
  },
  email: {
    id: "guestIntakeConfirmInfo.email",
    defaultMessage: "Email",
  },
  confirmButton: {
    id: "guestIntakeConfirmInfo.confirmButton",
    defaultMessage: "Confirm",
  },
  editButton: {
    id: "guestIntakeConfirmInfo.editButton",
    defaultMessage: "Edit",
  },
  saveButton: {
    id: "guestIntakeConfirmInfo.saveButton",
    defaultMessage: "Save",
  },
  cancelButton: {
    id: "guestIntakeConfirmInfo.cancelButton",
    defaultMessage: "Cancel",
  },
  backButton: {
    id: "guestIntakeConfirmInfo.backButton",
    defaultMessage: "Back",
  },
  fieldRequired: {
    id: "guestIntakeConfirmInfo.fieldRequired",
    defaultMessage: "Please fill in this field.",
  },
  phoneInvalid: {
    id: "guestIntakeConfirmInfo.phoneInvalid",
    defaultMessage: "Please enter a valid phone number.",
  },
  emailInvalid: {
    id: "guestIntakeConfirmInfo.emailInvalid",
    defaultMessage: "Please enter a valid email address.",
  },
  prefillUnavailable: {
    id: "guestIntakeConfirmInfo.prefillUnavailable",
    defaultMessage:
      "We couldn't load your test details just now. Your answers are safe — try again, or continue and check the details below.",
  },
  prefillRetry: {
    id: "guestIntakeConfirmInfo.prefillRetry",
    defaultMessage: "Try again",
  },
});

interface InputFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  inputType?: "text" | "email" | "tel";
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

const InputField: FC<InputFieldProps> = ({
  label,
  value,
  onChange,
  onPaste,
  inputType = "text",
  placeholder,
  error,
  disabled = false,
  required = false,
}) => (
  <div className="intake-form__field">
    <label className="intake-form__field__label">
      {label}
      {required && (
        <span className="intake-form__field__required" aria-hidden>
          *
        </span>
      )}
    </label>
    <input
      type={inputType}
      className={`intake-form__field__input ${error ? "intake-form__field__input--error" : ""}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onPaste={onPaste}
      placeholder={placeholder}
      disabled={disabled}
      aria-invalid={!!error}
      aria-required={required || undefined}
      aria-describedby={error ? `${label}-error` : undefined}
    />
    {error && (
      <span
        id={`${label}-error`}
        className="intake-form__field__error"
        role="alert"
      >
        {error}
      </span>
    )}
  </div>
);

/**
 * Guest variant of the GIS intake "confirm contact info" step (step 1 of the
 * anonymous order-lookup funnel). Unlike the authenticated step it depends on
 * none of the authenticated providers (UserContext / ProfileContext /
 * TestHistoryContext) and never handles the no-order flow — a guest always has
 * exactly one matched order. Prefill + session persistence go through the
 * token-authorized NonAuth resolvers.
 */
const GuestIntakeConfirmInfo: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { config } = useContext(ConfigContext);
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();

  const {
    token: guestToken,
    status: guestStatus,
    counselingIntent: guestCounselingIntent,
  } = useGisGuest();
  const {
    confirmedPhone: contextPhone,
    confirmedEmail: contextEmail,
    setConfirmedContact,
    setPrefilledData,
    setCounselingIntent,
    totalSteps,
    getStepNumber,
    getNextStep,
  } = useIntakeForm();
  const { getActiveSession, updateStep, markIntakeStarted } =
    useGisSessionOps();

  const {
    intakeFormData,
    isLoading: intakeFormLoading,
    error: intakeFormError,
    fetchIntakeFormData,
  } = useGisIntakeFormData(
    orderUidPublic ?? null,
    testUid ?? null,
    guestToken ?? null
  );

  // The order service can be briefly unreachable, which leaves the form with
  // only what the visitor typed in the lookup. Say so and offer another go,
  // rather than showing a half-filled form as if it were complete.
  const retryPrefill = () => {
    if (!orderUidPublic || !testUid) return;
    fetchIntakeFormData(orderUidPublic, testUid);
  };

  // Contact the visitor typed in the order-lookup funnel, stashed just before
  // navigating here. Used when the server-side order prefill is missing phone /
  // email (or the NonAuth prefill query fails entirely).
  const lookupPrefill = useMemo(() => readGuestLookupPrefill(), []);

  useEffect(() => {
    if (!intakeFormData && !lookupPrefill) return;
    if (intakeFormLoading) return;

    setPrefilledData({
      firstName: intakeFormData?.firstName || lookupPrefill?.firstName || null,
      lastName: intakeFormData?.lastName || lookupPrefill?.lastName || null,
      dateOfBirth:
        intakeFormData?.dateOfBirth || lookupPrefill?.dateOfBirth || null,
      // Preserve what the visitor entered in lookup; order data is fallback.
      phone: lookupPrefill?.phone || intakeFormData?.phone || null,
      email: lookupPrefill?.email || intakeFormData?.email || null,
      zipCode: lookupPrefill?.zipCode || intakeFormData?.zipCode || null,
      physicianClinic: intakeFormData?.physicianClinic ?? null,
      orderingPhysician: intakeFormData?.orderingPhysician ?? null,
      cancerType: intakeFormData?.cancerType ?? null,
      cancerStage: intakeFormData?.cancerStage ?? null,
      testType: intakeFormData?.testType ?? null,
      hasGISAppointment: intakeFormData?.hasGISAppointment ?? null,
      hasPreviouslyTakenTest: intakeFormData?.hasPreviouslyTakenTest ?? null,
    });
  }, [intakeFormData, intakeFormLoading, lookupPrefill, setPrefilledData]);

  // Classify pre/post-test counseling intent from the matched order's status
  // so downstream steps and the Acuity submission read a consistent label.
  useEffect(() => {
    if (!guestCounselingIntent && !guestStatus) return;
    setCounselingIntent(
      guestCounselingIntent ?? classifyCounselingIntent(guestStatus)
    );
  }, [guestCounselingIntent, guestStatus, setCounselingIntent]);

  const { validatePhone: validatePhoneFormat } = usePhoneValidation();

  const [isEditMode, setIsEditMode] = useState(false);
  const [phoneValue, setPhoneValue] = useState("");
  const [emailValue, setEmailValue] = useState("");
  const [savedPhone, setSavedPhone] = useState("");
  const [savedEmail, setSavedEmail] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");

  const validatePhoneField = (phone: string): string => {
    const cleaned = stripPhoneFormatting(phone);
    if (!cleaned || cleaned === "+") {
      return intl.formatMessage(messages.fieldRequired);
    }
    if (!validatePhoneFormat(cleaned)) {
      return intl.formatMessage(messages.phoneInvalid);
    }
    return "";
  };

  const usePlusSignInEmailAddress =
    config.test.usePlusSignInEmailAddress.enabled;

  const validateEmailField = (email: string): string => {
    if (!email || email.trim() === "") {
      return intl.formatMessage(messages.fieldRequired);
    }
    if (!validateEmail(email, usePlusSignInEmailAddress)) {
      return intl.formatMessage(messages.emailInvalid);
    }
    return "";
  };

  const currentStep = getStepNumber(GisIntakeStep.CONFIRM_INFO);
  const progressPercentage = (currentStep / totalSteps) * 100;

  // Prefer user-confirmed contact (context), then lookup input, then order prefill.
  useEffect(() => {
    if (contextPhone && contextEmail) {
      const phone = formatPhoneNumberInput(contextPhone);
      setPhoneValue(phone);
      setEmailValue(contextEmail);
      setSavedPhone(phone);
      setSavedEmail(contextEmail);
      return;
    }

    const phoneSource =
      lookupPrefill?.phone || contextPhone || intakeFormData?.phone || "";
    const emailSource =
      lookupPrefill?.email || contextEmail || intakeFormData?.email || "";

    if (!phoneSource && !emailSource) return;

    const phone = formatPhoneNumberInput(phoneSource);
    setPhoneValue(phone);
    setEmailValue(emailSource);
    setSavedPhone(phone);
    setSavedEmail(emailSource);
  }, [
    intakeFormData?.phone,
    intakeFormData?.email,
    lookupPrefill?.phone,
    lookupPrefill?.email,
    contextPhone,
    contextEmail,
  ]);

  const handleEdit = () => {
    setPhoneError("");
    setEmailError("");
    setIsEditMode(true);
  };

  const handleSave = () => {
    const phoneValidationError = validatePhoneField(phoneValue);
    const emailValidationError = validateEmailField(emailValue);
    setPhoneError(phoneValidationError);
    setEmailError(emailValidationError);
    if (phoneValidationError || emailValidationError) return;
    setSavedPhone(phoneValue);
    setSavedEmail(emailValue);
    setIsEditMode(false);
  };

  const handleCancelEdit = () => {
    setPhoneValue(savedPhone);
    setEmailValue(savedEmail);
    setPhoneError("");
    setEmailError("");
    setIsEditMode(false);
  };

  const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    setPhoneValue(formatPastedPhoneNumber(e.clipboardData.getData("text")));
    if (phoneError) setPhoneError("");
  };

  const handlePhoneChange = (value: string) => {
    setPhoneValue(formatPhoneNumberInput(value));
    if (phoneError) setPhoneError("");
  };

  const handleEmailChange = (value: string) => {
    setEmailValue(value);
    if (emailError) setEmailError("");
  };

  const handleBack = () => {
    writeGuestLookupPrefill({
      firstName: lookupPrefill?.firstName ?? intakeFormData?.firstName ?? "",
      lastName: lookupPrefill?.lastName ?? intakeFormData?.lastName ?? "",
      dateOfBirth:
        lookupPrefill?.dateOfBirth ?? intakeFormData?.dateOfBirth ?? "",
      phone: stripPhoneFormatting(phoneValue),
      email: emailValue.trim(),
      zipCode: lookupPrefill?.zipCode ?? intakeFormData?.zipCode ?? "",
      lookupIdentifier: lookupPrefill?.lookupIdentifier,
    });
    // The funnel screen that sent them here — the list of their tests, or the
    // lookup form. Stepping back through history would instead return whoever
    // walked forward into the intake and back out to the step they came from.
    navigate(resolveLookupReturnUrl());
  };

  const handleConfirm = () => {
    const phoneValidationError = validatePhoneField(phoneValue);
    const emailValidationError = validateEmailField(emailValue);
    setPhoneError(phoneValidationError);
    setEmailError(emailValidationError);
    if (phoneValidationError || emailValidationError) return;

    const strippedPhone = stripPhoneFormatting(phoneValue);
    const trimmedEmail = emailValue.trim();
    setConfirmedContact(strippedPhone, trimmedEmail);
    writeGuestLookupPrefill({
      firstName: lookupPrefill?.firstName ?? intakeFormData?.firstName ?? "",
      lastName: lookupPrefill?.lastName ?? intakeFormData?.lastName ?? "",
      dateOfBirth:
        lookupPrefill?.dateOfBirth ?? intakeFormData?.dateOfBirth ?? "",
      phone: strippedPhone,
      email: trimmedEmail,
      zipCode: lookupPrefill?.zipCode ?? intakeFormData?.zipCode ?? "",
      lookupIdentifier: lookupPrefill?.lookupIdentifier,
    });

    // Fire-and-forget: persist the confirmed contact to the guest triage row
    // (created up-front by the order-lookup funnel) and stamp intake_started.
    // Never block scheduling on this — the final save in the scheduler retries
    // the contact update.
    if (orderUidPublic && testUid) {
      void (async () => {
        try {
          const session = await getActiveSession({ orderUidPublic, testUid });
          const sessionId = session?.id;
          if (sessionId == null) return;
          await updateStep({
            sessionId,
            input: {},
            confirmedPhone: strippedPhone,
            confirmedEmail: trimmedEmail,
            prefilledInput: {
              ...buildLookupPrefilledInput(lookupPrefill, validatePhoneFormat),
              phone: strippedPhone,
              email: trimmedEmail,
            },
          });
          await markIntakeStarted({ sessionId });
        } catch (err) {
          console.error("Failed to persist guest confirmed contact:", err);
        }
      })();
    }

    const nextStep =
      getNextStep(GisIntakeStep.CONFIRM_INFO) ?? GisIntakeStep.REASON;
    navigate(
      routes.scheduleAGeneticInformationSessionIntakePage(
        orderUidPublic || "",
        testUid || ""
      ) + STEP_ROUTE_SUFFIX[nextStep]
    );
  };

  return (
    <div className="intake-form">
      <SpinnerView isLoading={intakeFormLoading} />
      <div className="intake-form__header">
        <button
          className="intake-form__header__back-button"
          onClick={handleBack}
          aria-label="Go back"
        >
          <Svg content={ArrowBack} />
        </button>
        <h1 className="intake-form__header__title">
          {intl.formatMessage(messages.pageTitle)}
        </h1>
        <div className="intake-form__header__placeholder" />
      </div>

      <div className="intake-form__content">
        <div className="intake-form__card-container">
          <div className="intake-form__progress-bar">
            <div
              className="intake-form__progress-bar__fill"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          {intakeFormError && !intakeFormData && !intakeFormLoading && (
            <div className="intake-form__prefill-error" role="alert">
              <span>{intl.formatMessage(messages.prefillUnavailable)}</span>
              <button
                type="button"
                className="intake-form__prefill-error__retry"
                onClick={retryPrefill}
              >
                {intl.formatMessage(messages.prefillRetry)}
              </button>
            </div>
          )}

          <div className="intake-form__card">
            <div className="intake-form__title-section">
              <span className="intake-form__step-indicator">
                {intl.formatMessage(messages.stepIndicator, {
                  current: currentStep,
                  total: totalSteps,
                })}
              </span>
              <h2 className="intake-form__main-title">
                {intl.formatMessage(messages.mainTitle)}
              </h2>
            </div>

            <div className="intake-form__fields">
              <InputField
                label={intl.formatMessage(messages.mobileNumber)}
                value={phoneValue}
                onChange={handlePhoneChange}
                onPaste={handlePhonePaste}
                inputType="tel"
                placeholder="+1-555-123-4567"
                error={phoneError}
                disabled={!isEditMode}
                required
              />
              <InputField
                label={intl.formatMessage(messages.email)}
                value={emailValue}
                onChange={handleEmailChange}
                inputType="email"
                placeholder="jsmith@gmail.com"
                error={emailError}
                disabled={!isEditMode}
                required
              />
            </div>

            <div className="intake-form__actions">
              {isEditMode ? (
                <>
                  <Button
                    className="intake-form__actions__confirm"
                    unelevated
                    onClick={handleSave}
                  >
                    {intl.formatMessage(messages.saveButton)}
                  </Button>
                  <Button
                    className="intake-form__actions__edit"
                    outlined
                    onClick={handleCancelEdit}
                  >
                    {intl.formatMessage(messages.cancelButton)}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    className="intake-form__actions__confirm"
                    unelevated
                    onClick={handleConfirm}
                  >
                    {intl.formatMessage(messages.confirmButton)}
                  </Button>
                  <Button
                    className="intake-form__actions__edit"
                    outlined
                    onClick={handleEdit}
                  >
                    {intl.formatMessage(messages.editButton)}
                  </Button>
                  <button
                    className="intake-form__actions__back"
                    onClick={handleBack}
                  >
                    {intl.formatMessage(messages.backButton)}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestIntakeConfirmInfo;
